import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { Textarea } from "src/components/base/textarea";
import { ApiInternal } from "src/api/api_internal";
import { UUID } from "src/api/api_common";
import { SimpleTagDirectory } from "src/api/generated/frontend_internal_v1";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import WS from "src/api/ws";

/**
 * The properties for {@link CreateTagDialog}
 */
export type CreateTagDialogProps = {
    /**
     * Directory to pre-select.
     *
     * The user *can* change the directory.
     */
    directory_uuid: UUID | undefined;
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the form is submitted */
    onSubmit?: (result: {
        /** The directory the tag was created in (if any) */
        directory_uuid: UUID | undefined;
        /** The created tag's uuid */
        tagUuid: UUID;
    }) => void;

    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new tag
 */
export default function CreateTagDialog(props: CreateTagDialogProps) {
    const [t] = useTranslation("dialog-create-tag");
    const [tg] = useTranslation();

    const [directories, setDirectories] = React.useState<SimpleTagDirectory[]>([]);

    const form = useForm({
        defaultValues: { name: "", description: "", directory: props.directory_uuid ?? "ungrouped" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.directory !== "ungrouped" && !directories.map((dir) => dir.uuid).includes(value.directory)) {
                    value.directory = "";
                    return { fields: { directory: t("error.no-target-selected") } };
                }

                const res = await ApiInternal.tags.create({
                    name: value.name,
                    description: value.description,
                    directory: value.directory === "ungrouped" ? undefined : value.directory,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error.name_not_unique ? t("error.name-not-unique") : undefined,
                            description: undefined,
                        },
                    };
                }

                if (props.onSubmit) {
                    props.onSubmit({
                        directory_uuid: value.directory === "ungrouped" ? undefined : value.directory,
                        tagUuid: res.value.uuid,
                    });
                }
            },
        },
    });

    const [refreshKey, setRefreshKey] = React.useState(0);

    // Trigger a bump of the counter "refreshKey" when tags change, so that it re-fetches the directories in the second useEffect
    useEffect(() => {
        const listener = WS.addEventListener("message.TagsChanged", () => {
            setRefreshKey((k) => k + 1);
        });
        return () => WS.removeEventListener(listener);
    }, [props.isOpen]);

    useEffect(() => {
        let cancelled = false;
        ApiInternal.tags.getAllDirectories().then((res) => {
            if (!cancelled) setDirectories(res.list);
        });
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.directory_uuid]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-tag")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Description>{t("description.name")}</Description>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            pattern={"[a-z0-9]+(-*[a-z0-9]+)*"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Textarea
                                            value={fieldApi.state.value}
                                            rows={4}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            resizable={false}
                                        />
                                    </Field>
                                )}
                            </form.Field>

                            <form.Field name={"directory"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.directory")}</RequiredLabel>
                                        <Listbox
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e)}
                                        >
                                            <ListboxOption value={"ungrouped"}>
                                                <ListboxLabel>
                                                    <span className={"pr-0.5 italic"}>{t("label.ungrouped")}</span>
                                                </ListboxLabel>
                                            </ListboxOption>
                                            {directories &&
                                                directories.map((d) => (
                                                    <ListboxOption value={d.uuid} key={d.uuid}>
                                                        <ListboxLabel>{d.name}</ListboxLabel>
                                                    </ListboxOption>
                                                ))}
                                        </Listbox>
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.create-tag")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
